import React, { useState } from 'react';
import { Button, Divider, Form, Input, message, Space } from 'antd';
import { CommunitySummary } from '@quick/client';

import { ReactComponent as JoinSuccessSvg } from 'assets/svg/join_success.svg';
import ModalLayout from '../components/ModalLayout';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import { auth } from '../../../auth/firebaseAuth';
import { useCurrentUser, useMyMembers } from '../../../redux/selector';
import CommunityInfoOverview from '../components/CommunityInfoOverview';
import useDebounce from '../../../hooks/UseDebounce';
import { useCommunitySummariesQuery, useCommunityMutation } from '../../../hooks/Community';

export const PENDING_APPROVAL_HEADER = 'Your request is being reviewed by community admins';
export const PENDING_APPROVAL_SUBTITLE = 'We’ll email you when there are updates.';

interface CommunityListItemProps {
  community: CommunitySummary;
  joined: boolean;
  onSelect: (community: CommunitySummary) => void;
}

const CommunityListItem = ({ community, onSelect, joined }: CommunityListItemProps) => {
  return (
    <>
      <div className="d-flex flex-row align-items-center">
        <div className="flex-grow-1 d-flex flex-column">
          <CommunityInfoOverview community={community} />
        </div>
        <Button disabled={joined} onClick={() => onSelect(community)} style={{ justifySelf: 'end' }} type="primary">
          Join
        </Button>
      </div>
      <Divider className="my-3" />
    </>
  );
};

enum STEP {
  FIND_COMMUNITY,
  ENTER_INFORMATION,
  SUCCESS,
}

interface JoinFlowProps {
  onComplete: () => void;
  switchFlow: () => void;
}

interface FormFields {
  name: string;
  email: string;
  address: string;
  personalIntro: string;
}

const JoinFlow = ({ onComplete, switchFlow }: JoinFlowProps) => {
  const members = useMyMembers();
  const joinedCommunities = members.map((it) => it.communityId);
  const [step, setStep] = useState(STEP.FIND_COMMUNITY);
  const [search, setSearch] = useState('');
  const [community, setCommunity] = useState<CommunitySummary>();
  const [form] = useForm<FormFields>();

  const user = useCurrentUser();

  const debouncedSearch = useDebounce(search, 300);
  const communitiesQuery = useCommunitySummariesQuery(debouncedSearch);

  const { joinCommunityMutation } = useCommunityMutation();

  const onSelectCommunity = (community: CommunitySummary) => {
    setCommunity(community);
    setStep(STEP.ENTER_INFORMATION);
  };

  const onSubmit = async () => {
    try {
      await form.validateFields();
      await joinCommunityMutation.mutateAsync({
        joinRequest: {
          ...form.getFieldsValue(),
          communityId: community!.communityId,
        },
      });
      setStep(STEP.SUCCESS);
    } catch (e) {
      message.error(`Failed to join community`);
    }
  };

  switch (step) {
    case STEP.FIND_COMMUNITY:
      return (
        <ModalLayout
          header={<h6>Enter your community or HOA name</h6>}
          content={
            <>
              <Input
                className="mt-3"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="My wonderful HOA"
              />
              <div className="flex-grow-1 mt-4">
                {communitiesQuery.data?.content.map((c, i) => (
                  <CommunityListItem
                    key={c.communityId}
                    community={c}
                    onSelect={onSelectCommunity}
                    joined={joinedCommunities.includes(c.communityId)}
                  />
                ))}
              </div>
            </>
          }
          footer={
            <>
              <span>Cannot find you community?</span>
              <Button onClick={switchFlow} type="primary" ghost>
                Create your community
              </Button>
            </>
          }
        />
      );

    case STEP.ENTER_INFORMATION:
      return (
        <ModalLayout
          header={
            <h6>Tell {community?.name ? <span>{community?.name}</span> : <span>community</span>} admin about you</h6>
          }
          content={
            <Form
              initialValues={{
                name: auth.currentUser?.displayName,
                email: user?.email,
              }}
              layout="vertical"
              colon={false}
              form={form}
              requiredMark={false}
              preserve={false}
            >
              <Form.Item
                preserve
                required
                label="Name"
                name="name"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                preserve
                required
                label="Email"
                name="email"
                rules={[
                  {
                    required: true,
                  },
                  {
                    type: 'email',
                    message: 'Email is not valid',
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item label="Additional information" name="personalIntro">
                <TextArea autoSize={{ minRows: 3, maxRows: 10 }} />
              </Form.Item>
            </Form>
          }
          footer={
            <Space>
              <Button loading={joinCommunityMutation.isLoading} onClick={onSubmit} type="primary">
                Done
              </Button>
              <Button
                disabled={joinCommunityMutation.isLoading}
                onClick={() => setStep(STEP.FIND_COMMUNITY)}
                type="text"
              >
                Previous
              </Button>
            </Space>
          }
        />
      );

    case STEP.SUCCESS:
      return (
        <ModalLayout
          header={<h6>{PENDING_APPROVAL_HEADER}</h6>}
          content={
            <div className="flex-grow-1 d-flex flex-column ">
              <div className="paragraph-m">{PENDING_APPROVAL_SUBTITLE}</div>
              <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                <JoinSuccessSvg />
              </div>
            </div>
          }
          footer={
            <Button onClick={onComplete} type="primary">
              Done
            </Button>
          }
        />
      );
  }
};

export default JoinFlow;
